import { toast } from "sonner";
import { Link } from "react-router-dom";
import { BaseSheet } from "@/components/common/BaseSheet";
import { Button } from "@/components/ui/button";
import {
  useApplyDefaultPronunciations,
  useDefaultPronunciationsPreview,
} from "@/hooks/agent/usePronunciationVaults";

interface ApplyDefaultsSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ApplyDefaultsSheet = ({ isOpen, onClose }: ApplyDefaultsSheetProps) => {
  const { data: preview, isLoading, error } = useDefaultPronunciationsPreview(isOpen);
  const applyDefaults = useApplyDefaultPronunciations();

  const handleApply = () => {
    applyDefaults.mutate(undefined, {
      onSuccess: ({ genieCount }) => {
        toast.success(
          <span>
            Adding defaults to {genieCount} {genieCount === 1 ? "genie" : "genies"}.{" "}
            <Link to="/admin/bulk-update-jobs" className="underline">Track progress</Link>
          </span>,
        );
        onClose();
      },
      onError: (applyError) => toast.error(applyError.message),
    });
  };

  const hasWords = (preview?.words.length ?? 0) > 0;

  return (
    <BaseSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Apply defaults to all genies"
      description="Adds the default pronunciations to every voice genie on the platform, including customers' genies."
      size="lg"
      loading={isLoading}
      error={error ? (error as Error).message : null}
      footer={
        <div className="flex justify-start gap-3">
          <Button variant="outline" onClick={onClose} disabled={applyDefaults.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={!hasWords || applyDefaults.isPending}
            className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] hover:brightness-105 text-white border-0"
          >
            {applyDefaults.isPending
              ? "Starting..."
              : `Apply to ${preview?.genieCount ?? 0} ${preview?.genieCount === 1 ? "genie" : "genies"}`}
          </Button>
        </div>
      }
    >
      {preview && (
        <div className="space-y-6">
          {hasWords ? (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-gray-700">Words that will be added</p>
              {preview.words.map((pair) => (
                <div key={pair.word} className="flex justify-between gap-4 text-sm text-gray-600 border-b border-genie-border py-2">
                  <span className="font-medium text-gray-900">{pair.word}</span>
                  <span>{pair.pronunciation}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-600">
              No default words yet. Mark a vault as "Default for all genies" first.
            </p>
          )}
          <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
            <li>A word a genie already has keeps its own pronunciation.</li>
            <li>A default that a customer removed from their genie is added back.</li>
            <li>Genies that already have every word are skipped, so it is safe to run this again.</li>
          </ul>
        </div>
      )}
    </BaseSheet>
  );
};
